import { noop } from '@sgme/fp';
import { FormattedMessage } from 'components/shared/Intl';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { SponsoFee, SponsoFeeField } from 'state/quotes/sponsoFees/sponsoFees';
import type { PriceEnhancement, PriceEnhancementField, PriceEnhancementMode } from 'state/quotes/trades/trades.model';
import { roundNumberToPrecision } from 'utils/core/math';
import { capitalizeFirstLetterOnly } from 'utils/string';

import { CompactPercentFieldAdjuster } from '../../../shared/CompactPercentField/CompactPercentFieldAdjuster';
import { getEnhancedFixedAmountInPercent, getEnhancedOfferPrice, getEnhancedUpfrontFee } from './PriceEnhancerRules';

const increment = 0.1;

const precision = 4;

type SponsoFeeFieldAdjusterProps = {
  onValueChanged: (val: SponsoFee[SponsoFeeField]) => void;
  className: string;
  value?: SponsoFee[SponsoFeeField];
  field: SponsoFeeField;
  canBeEmpty?: boolean;
};
function SponsoFeeFieldAdjuster({
  className,
  value,
  field,
  onValueChanged,
  canBeEmpty = false,
}: SponsoFeeFieldAdjusterProps) {
  const { formatMessage } = useIntl();
  const capitalizedField = capitalizeFirstLetterOnly(field);
  const label = formatMessage({ id: `field.${capitalizedField}` });
  const handleValueChanged = useCallback(
    (v: SponsoFee[SponsoFeeField]) => {
      onValueChanged(v !== undefined ? roundNumberToPrecision(v / 100, precision + 2) : v);
    },
    [onValueChanged],
  );
  return (
    <div className={`${className} form-group mb-2`}>
      <label htmlFor={field} className="text-secondary">
        <FormattedMessage id={`form.${capitalizedField}`} />
      </label>
      <CompactPercentFieldAdjuster
        id={field}
        onValueChange={handleValueChanged}
        step={increment}
        placeholder={label}
        value={value}
        digits={precision}
        canBeEmpty={canBeEmpty}
      />
    </div>
  );
}

type PriceEnhancementFieldAdjusterProps = {
  onValueChanged: (val: PriceEnhancement[PriceEnhancementField]) => void;
  className: string;
  value: PriceEnhancement[PriceEnhancementField];
  field: PriceEnhancementField;
};
function PriceEnhancementFieldAdjuster({
  className,
  value,
  field,
  onValueChanged,
}: PriceEnhancementFieldAdjusterProps) {
  const { formatMessage } = useIntl();
  const capitalizedField = capitalizeFirstLetterOnly(field);
  const label = formatMessage({ id: `field.${capitalizedField}` });
  const handleValueChanged = useCallback(
    (v: PriceEnhancement[PriceEnhancementField]) => {
      onValueChanged(v !== undefined ? roundNumberToPrecision(v / 100, precision + 2) : v);
    },
    [onValueChanged],
  );
  return (
    <div className={`${className} form-group mb-2`}>
      <label htmlFor={field} className="text-secondary">
        <FormattedMessage id={`form.${capitalizedField}`} />
      </label>
      <CompactPercentFieldAdjuster
        id={field}
        onValueChange={handleValueChanged}
        step={increment}
        placeholder={label}
        value={value}
        digits={precision}
      />
    </div>
  );
}

export type PriceEnhancerRawProps = {
  mode: PriceEnhancementMode;
  displaySponsoFields?: boolean;
  onFieldValueChanged: (field: PriceEnhancementField, value: PriceEnhancement[PriceEnhancementField]) => void;
  onSponsoFeeFieldValueChanged?: (field: SponsoFeeField, value: SponsoFee[SponsoFeeField]) => void;
} & PriceEnhancement &
  SponsoFee;

export const PriceEnhancerRaw: FC<PriceEnhancerRawProps> = ({
  issuePrice,
  xMargin,
  tradingOffer,
  onFieldValueChanged,
  onSponsoFeeFieldValueChanged = noop,
  displaySponsoFields,
  mode,
  sponso,
  sponsoUpFront,
  yMarginRetrocession,
  topUpUpFront,
}) => {
  const reoffer = useMemo(
    () => roundNumberToPrecision(getEnhancedOfferPrice(tradingOffer, xMargin), precision + 2),
    [xMargin, tradingOffer],
  );
  const upFrontFee = useMemo(
    () => roundNumberToPrecision(getEnhancedUpfrontFee(issuePrice, tradingOffer, xMargin), precision + 2),
    [issuePrice, tradingOffer, xMargin],
  );

  const fixedAmountInPercent = useMemo(
    () => roundNumberToPrecision(getEnhancedFixedAmountInPercent(tradingOffer, xMargin), precision + 2),
    [tradingOffer, xMargin],
  );

  return (
    <div className="d-flex flex-wrap" data-e2e="price-adjustment">
      <div className="d-flex">
        <PriceEnhancementFieldAdjuster
          className="col-12 col-sm-4"
          field="xMargin"
          onValueChanged={v => onFieldValueChanged('xMargin', v)}
          value={xMargin}
        />
        <PriceEnhancementFieldAdjuster
          className="col-6 col-sm-4"
          field="tradingOffer"
          onValueChanged={v => onFieldValueChanged('tradingOffer', v)}
          value={tradingOffer}
        />
        {mode === 'UpfrontFee' && (
          <div className="col-6 col-sm-4 form-group mb-2">
            <label htmlFor="upFrontFee" className="text-secondary">
              <FormattedMessage id="form.UpfrontFee" />
            </label>
            <CompactPercentFieldAdjuster id="upFrontFee" value={upFrontFee} digits={precision} disabled />
          </div>
        )}
        {mode === 'Reoffer' && (
          <div className="col-6 col-sm-4 form-group mb-2">
            <label htmlFor="reoffer" className="text-secondary">
              Reoffer
            </label>
            <CompactPercentFieldAdjuster id="reoffer" value={reoffer} digits={precision} disabled />
          </div>
        )}
        {mode === 'Premium' && (
          <div className="col-6 col-sm-4 form-group mb-2">
            <label htmlFor="fixedAmountInPercent" className="text-secondary">
              <FormattedMessage id="form.FixedAmountInPercent" />
            </label>
            <CompactPercentFieldAdjuster
              id="fixedAmountInPercent"
              value={fixedAmountInPercent}
              digits={precision}
              disabled
            />
          </div>
        )}
      </div>
      {displaySponsoFields && (
        <div className="d-flex w-100">
          <SponsoFeeFieldAdjuster
            className="col-3 col-sm-3"
            field="sponso"
            onValueChanged={v => onSponsoFeeFieldValueChanged('sponso', v)}
            value={sponso}
            canBeEmpty={true}
          />{' '}
          <SponsoFeeFieldAdjuster
            className="col-3 col-sm-3"
            field="sponsoUpFront"
            onValueChanged={v => onSponsoFeeFieldValueChanged('sponsoUpFront', v)}
            value={sponsoUpFront}
            canBeEmpty={true}
          />{' '}
          <SponsoFeeFieldAdjuster
            className="col-3 col-sm-3"
            field="yMarginRetrocession"
            onValueChanged={v => onSponsoFeeFieldValueChanged('yMarginRetrocession', v)}
            value={yMarginRetrocession}
            canBeEmpty={true}
          />{' '}
          <SponsoFeeFieldAdjuster
            className="col-3 col-sm-3"
            field="topUpUpFront"
            onValueChanged={v => onSponsoFeeFieldValueChanged('topUpUpFront', v)}
            value={topUpUpFront}
            canBeEmpty={true}
          />
        </div>
      )}
    </div>
  );
};

PriceEnhancerRaw.displayName = 'PriceEnhancerRaw';
